import React, { useState, useEffect, useCallback } from 'react';
import { getImageUrl } from '../../services/api';
import './ScreenshotLightbox.css';

function ScreenshotLightbox({ screenshots, currentIndex, onClose, onNavigate }) {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  // Minimum swipe distance (in px) to trigger navigation
  const minSwipeDistance = 50;
  
  // Zoom settings - responsive based on device
  const minZoom = 1;
  const maxZoom = 5;
  const zoomStep = isMobile ? 0.5 : 0.25; // Larger steps on mobile
  
  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Navigation handlers
  const handlePrevious = useCallback(() => {
    const newIndex = currentIndex === 0 ? screenshots.length - 1 : currentIndex - 1;
    onNavigate(newIndex);
    // Reset zoom when changing images
    setZoom(1);
    setPanX(0);
    setPanY(0);
  }, [currentIndex, screenshots.length, onNavigate]);

  const handleNext = useCallback(() => {
    const newIndex = currentIndex === screenshots.length - 1 ? 0 : currentIndex + 1;
    onNavigate(newIndex);
    // Reset zoom when changing images
    setZoom(1);
    setPanX(0);
    setPanY(0);
  }, [currentIndex, screenshots.length, onNavigate]);

  const handleThumbnailClick = (index) => {
    onNavigate(index);
    // Reset zoom when changing images
    setZoom(1);
    setPanX(0);
    setPanY(0);
  };
  
  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + zoomStep, maxZoom));
  }, []);
  
  const handleZoomOut = useCallback(() => {
    const newZoom = Math.max(zoom - zoomStep, minZoom);
    setZoom(newZoom);
    if (newZoom === 1) {
      // Reset pan when zooming out to 1x
      setPanX(0);
      setPanY(0);
    }
  }, [zoom]);
  
  const handleZoomReset = useCallback(() => {
    setZoom(1);
    setPanX(0);
    setPanY(0);
  }, []);
  
  // Mouse wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  }, [handleZoomIn, handleZoomOut]);
  
  // Pan handlers (for zoomed images)
  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - panX, y: e.clientY - panY });
    }
  };
  
  const handleMouseMove = (e) => {
    if (isPanning && zoom > 1) {
      setPanX(e.clientX - panStart.x);
      setPanY(e.clientY - panStart.y);
    }
  };
  
  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // Touch gesture handlers (for navigation when not zoomed)
  const onTouchStart = (e) => {
    if (zoom === 1) {
      // Only allow swipe navigation when not zoomed
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientX);
    }
  };

  const onTouchMove = (e) => {
    if (zoom === 1 && touchStart) {
      setTouchEnd(e.targetTouches[0].clientX);
    }
  };

  const onTouchEnd = () => {
    if (zoom === 1 && touchStart && touchEnd) {
      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > minSwipeDistance;
      const isRightSwipe = distance < -minSwipeDistance;
      
      if (isLeftSwipe) {
        handleNext();
      } else if (isRightSwipe) {
        handlePrevious();
      }
    }
  };

  // Keyboard navigation and zoom
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && zoom === 1) {
        // Only navigate when not zoomed
        handlePrevious();
      } else if (e.key === 'ArrowRight' && zoom === 1) {
        // Only navigate when not zoomed
        handleNext();
      } else if (e.key === 'Home') {
        onNavigate(0);
      } else if (e.key === 'End') {
        onNavigate(screenshots.length - 1);
      } else if (e.key === '+' || e.key === '=') {
        // Zoom in with + or =
        handleZoomIn();
      } else if (e.key === '-' || e.key === '_') {
        // Zoom out with - or _
        handleZoomOut();
      } else if (e.key === '0') {
        // Reset zoom with 0
        handleZoomReset();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrevious, handleNext, screenshots.length, onClose, onNavigate, handleZoomIn, handleZoomOut, handleZoomReset, zoom]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (screenshots.length === 0) return null;

  const currentScreenshot = screenshots[currentIndex];

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <div 
        className="lightbox-container" 
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Close button */}
        <button className="lightbox-close" onClick={onClose} title="Close (ESC)">
          ✕
        </button>
        
        {/* Zoom controls */}
        <div className="lightbox-zoom-controls">
          <button 
            className="lightbox-zoom-btn" 
            onClick={handleZoomOut}
            disabled={zoom <= minZoom}
            title="Zoom Out (- key)"
          >
            −
          </button>
          <span className="lightbox-zoom-level">{Math.round(zoom * 100)}%</span>
          <button 
            className="lightbox-zoom-btn" 
            onClick={handleZoomIn}
            disabled={zoom >= maxZoom}
            title="Zoom In (+ key)"
          >
            +
          </button>
          {zoom > 1 && (
            <button 
              className="lightbox-zoom-btn lightbox-zoom-reset" 
              onClick={handleZoomReset}
              title="Reset Zoom (0 key)"
            >
              ↺
            </button>
          )}
        </div>

        {/* Main image */}
        <div 
          className="lightbox-main"
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ cursor: zoom > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default' }}
        >
          {screenshots.length > 1 && zoom === 1 && (
            <>
              <button 
                className="lightbox-nav lightbox-nav-prev" 
                onClick={handlePrevious}
                title="Previous (← or swipe right)"
              >
                ‹
              </button>
              <button 
                className="lightbox-nav lightbox-nav-next" 
                onClick={handleNext}
                title="Next (→ or swipe left)"
              >
                ›
              </button>
            </>
          )}

          <img
            src={getImageUrl(currentScreenshot.filePath)}
            alt={`Screenshot by ${currentScreenshot.username}`}
            className="lightbox-image"
            style={{
              transform: `scale(${zoom}) translate(${panX / zoom}px, ${panY / zoom}px)`,
              transition: isPanning ? 'none' : 'transform 0.2s ease-out',
              maxWidth: zoom === 1 ? (isMobile ? '100vw' : '100%') : 'none',
              maxHeight: zoom === 1 ? (isMobile ? 'calc(100vh - 200px)' : '100%') : 'none',
              width: zoom > 1 ? (isMobile ? '100vw' : '100%') : 'auto',
              height: zoom > 1 ? 'auto' : 'auto',
              objectFit: zoom === 1 ? 'contain' : (isMobile ? 'contain' : 'cover')
            }}
            draggable={false}
          />
        </div>

        {/* Metadata */}
        <div className="lightbox-metadata">
          <div className="lightbox-info">
            <strong>{currentScreenshot.displayName || currentScreenshot.username}</strong>
            <span className="lightbox-separator">•</span>
            <span>{new Date(currentScreenshot.capturedAt).toLocaleString()}</span>
            {currentScreenshot.uploadTimeMs && (
              <>
                <span className="lightbox-separator">•</span>
                <span>⚡ {currentScreenshot.uploadTimeMs}ms</span>
              </>
            )}
            {currentScreenshot.metadata?.compressionProfile && (
              <>
                <span className="lightbox-separator">•</span>
                <span>🎯 {currentScreenshot.metadata.compressionProfile}</span>
              </>
            )}
          </div>
          <div className="lightbox-counter">
            {currentIndex + 1} / {screenshots.length}
          </div>
        </div>

        {/* Thumbnail strip */}
        {screenshots.length > 1 && (
          <div className="lightbox-thumbnails">
            <div className="lightbox-thumbnails-container">
              {screenshots.map((screenshot, index) => (
                <div
                  key={screenshot.id}
                  className={`lightbox-thumbnail ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => handleThumbnailClick(index)}
                  style={{ 
                    backgroundImage: `url(${getImageUrl(screenshot.filePath)})` 
                  }}
                  title={`${screenshot.displayName || screenshot.username} - ${new Date(screenshot.capturedAt).toLocaleString()}`}
                >
                  {index === currentIndex && <div className="thumbnail-indicator"></div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ScreenshotLightbox;

